// @ts-nocheck
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Clock, DollarSign, TrendingUp, Target } from "lucide-react";
import { Link } from "wouter";

// /api/dashboard/kpis sends a light-mode class (text-green-600 / text-red-600) for the change text.
const changeColorClass = (color: string) =>
  color.includes("green") ? "text-green-600 dark:text-green-400"
  : color.includes("red") ? "text-red-600 dark:text-red-400"
  : color;

export default function KPICards() {
  const { data: kpis, isLoading } = useQuery({
    queryKey: ["/api/dashboard/kpis"],
    refetchInterval: 60000, // Refresh every minute
  });

  const kpiItems = [
    {
      title: "Open Claims",
      value: kpis?.openClaims || 0,
      icon: Clock,
      iconBg: "bg-amber-100 dark:bg-amber-500/10",
      iconColor: "text-amber-600 dark:text-amber-400",
      change: kpis?.openClaimsChange || "+0%",
      changeColor: kpis?.openClaimsChangeColor || "text-muted-foreground",
      testId: "kpi-open-claims",
    },
    {
      title: "Total Pipeline",
      value: kpis?.totalPipeline || "$0",
      icon: Target,
      iconBg: "bg-purple-100 dark:bg-purple-500/10",
      iconColor: "text-purple-600 dark:text-purple-400",
      change: kpis?.totalPipelineChange || "+0%",
      changeColor: kpis?.totalPipelineChangeColor || "text-muted-foreground",
      testId: "kpi-total-pipeline",
    },
    {
      title: "Settlement Value",
      value: kpis?.settlementValue || "$0",
      icon: DollarSign,
      iconBg: "bg-green-100 dark:bg-green-500/10",
      iconColor: "text-green-600 dark:text-green-400",
      change: kpis?.settlementValueChange || "+0%",
      changeColor: kpis?.settlementValueChangeColor || "text-muted-foreground",
      testId: "kpi-settlement-value",
    },
    {
      title: "Avg. Resolution",
      value: `${kpis?.avgResolutionDays || 0} days`,
      icon: TrendingUp,
      iconBg: "bg-blue-100 dark:bg-blue-500/10",
      iconColor: "text-blue-600 dark:text-blue-400",
      change: kpis?.avgResolutionDaysChange || "0 days",
      changeColor: kpis?.avgResolutionDaysChangeColor || "text-muted-foreground",
      testId: "kpi-avg-resolution",
    },
  ];

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
        {[...Array(4)].map((_, i) => (
          <Card key={i}>
            <CardContent className="p-4 sm:p-6">
              <div className="animate-pulse">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="h-4 bg-muted rounded w-24 mb-2"></div>
                    <div className="h-8 bg-muted rounded w-16"></div>
                  </div>
                  <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-muted"></div>
                </div>
                <div className="h-3 bg-muted rounded w-20 mt-2"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
      {kpiItems.map((item) => {
        const Icon = item.icon;

        return (
          <Card key={item.title}>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm font-medium text-muted-foreground truncate">
                    {item.title}
                  </p>
                  {item.title === "Open Claims" ? (
                    <Link
                      href="/claims?status=open"
                      className="hover:underline cursor-pointer"
                    >
                      <p
                        className="text-2xl sm:text-3xl font-bold text-foreground transition-colors hover:text-primary"
                        data-testid={item.testId}
                      >
                        {item.value}
                      </p>
                    </Link>
                  ) : (
                    <p
                      className="text-2xl sm:text-3xl font-bold text-foreground"
                      data-testid={item.testId}
                    >
                      {item.value}
                    </p>
                  )}
                </div>
                <div className={`h-8 w-8 sm:h-10 sm:w-10 rounded-full ${item.iconBg} flex items-center justify-center flex-shrink-0 ml-2`}>
                  <Icon className={`${item.iconColor} h-4 w-4 sm:h-5 sm:w-5`} />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                <span className={changeColorClass(item.changeColor)}>{item.change}</span> from last month
              </p>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
